import React, { useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AutoHeight from 'embla-carousel-auto-height';
import Autoplay from 'embla-carousel-autoplay';
import { toast } from "sonner";
import {
  TrendingUp,
  Users,
  Lightbulb,
  Search,
  DollarSign,
  Globe,
  Cpu,
  GraduationCap,
  Briefcase,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

import heroTeam from "../assets/hero_team.jpg";
import uiHome from "../assets/ui_home.png";
import uiOffers from "../assets/ui_offers.png";
import uiDashboard from "../assets/ui_dashboard.png";
import uiCompanies from "../assets/ui_companies.png";
import mapWorld from "../assets/map_world.png";
import flagKorea from "../assets/flag_korea.png";
import flagMalaysia from "../assets/flag_malaysia.png";
import flagSingapore from "../assets/flag_singapore.png";
import flagChina from "../assets/flag_china.png";
import thomasPortrait from "../assets/thomas_portrait.jpg";
import thomasFushimi from "../assets/thomas_fushimi.jpg";
import thomasExhibition from "../assets/thomas_exhibition.jpg";
import gregoireJapan from "../assets/gregoire_japan.jpg";
import diegoTokyo from "../assets/diego_tokyo.png";
import yoannJapan from "../assets/yoann_japan.jpg";
import mirabelleJapan from "../assets/mirabelle_japan.jpg";

const InterfaceItem = ({ item, i }) => {
  return (
    <div className={`flex flex-col lg:flex-row gap-12 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
      <div className="w-full lg:w-1/2">
        <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200 bg-white group hover:shadow-3xl transition-all duration-500">
          <img
            src={item.img}
            alt={item.title}
            className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
          />
        </div>
      </div>
      <div className="w-full lg:w-1/2 space-y-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600 font-bold text-lg mb-2">
          {i + 1}
        </div>
        <h3 className="text-3xl font-bold text-slate-900">{item.title}</h3>
        <p className="text-xl text-slate-600 leading-relaxed">{item.text}</p>
      </div>
    </div>
  );
};

const HomeFR = () => {
  const handleDemoClick = (action) => {
    toast.info(`La fonctionnalité ${action} sera bientôt disponible !`, {
      description: "Ceci est un site de démonstration."
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 z-10">
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-600">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-500"></span>
                </span>
                Startup Pré-seed
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
                Recrutez le <span className="text-red-600">top des étudiants français</span> pour vos équipes au Japon
              </h1>
              <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
                Ingénierie, Commerce, Marketing, Tourisme & Hôtellerie. Recrutez sans effort : connectez-vous à un réseau exclusif de candidats pré-qualifiés, motivés et prêts à rejoindre vos équipes.
              </p>
              <div className="flex flex-col gap-3 pt-2">
                <p className="text-lg font-medium text-slate-700 italic">
                  "Empowering Interns. Elevating Employers."
                </p>
              </div>
            </div>
            <div className="relative lg:h-[600px] w-full rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={heroTeam}
                alt="Équipe diversifiée collaborant dans un bureau à Tokyo"
                className="object-cover w-full h-full transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem / Data-Driven Hook */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Transformez la pénurie de talents en opportunité de croissance</h2>
            <p className="text-lg text-slate-600">
              Face à une pénurie critique de main-d'œuvre au Japon (ratio offres/demandes de 1,28) et un déficit prévu de 790 000 profils IT d'ici 2030, les méthodes de recrutement traditionnelles ne suffisent plus. Elles négligent souvent une ressource précieuse : les talents internationaux.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg bg-slate-50">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <TrendingUp className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Ratio Offres/Demandes de 1,28</h3>
                <p className="text-slate-600">
                  Le ratio offres d'emploi/demandeurs au Japon reste élevé, rendant la concurrence pour les talents locaux qualifiés féroce.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-slate-50">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <Users className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Pénurie de 790 000 en IT</h3>
                <p className="text-slate-600">
                  Le METI projette une pénurie allant jusqu'à 790 000 personnels IT d'ici 2030. La croissance de votre entreprise dépend de la découverte de nouveaux viviers de talents.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Intern Advantage */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Une stratégie de recrutement intelligente, flexible et rentable</h2>
            <p className="text-lg text-slate-300 max-w-2xl">
              Dans un marché où les profils seniors sont rares et coûteux, les talents étudiants offrent une alternative puissante.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <GraduationCap className="h-6 w-6 text-red-400" />,
                title: "Formez Vos Futurs Leaders",
                desc: "Intérez des jeunes talents, formez-les à vos méthodes et fidélisez-les sur le long terme."
              },
              {
                icon: <Lightbulb className="h-6 w-6 text-red-400" />,
                title: "Injectez de l'Innovation",
                desc: "Bénéficiez de perspectives nouvelles et de compétences techniques à jour (notamment en ingénierie et digital)."
              },
              {
                icon: <Search className="h-6 w-6 text-red-400" />,
                title: "Réduisez les Risques",
                desc: "Le stage est la meilleure période d'essai. Évaluez les compétences et le \"fit\" culturel avant un recrutement définitif."
              },
              {
                icon: <DollarSign className="h-6 w-6 text-red-400" />,
                title: "Rentabilité & Flexibilité",
                desc: "Une solution agile pour renforcer vos équipes sans les contraintes d'un recrutement classique."
              }
            ].map((item, index) => (
              <div key={index} className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-red-500/50 transition-colors">
                <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4 border border-slate-700">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">Notre Solution</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">La seule plateforme dédiée au pont France-Japon</h2>
            <p className="text-lg text-slate-600 mt-4">Nous ne sommes pas un simple job board. Nous facilitons chaque étape du processus.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              {
                title: "Vivier d'Excellence",
                desc: "Accès à +10 000 étudiants des grandes écoles (Ingénieurs, Business Schools).",
                icon: <Globe className="h-6 w-6 text-white" />
              },
              {
                title: "Matching par IA",
                desc: "Notre algorithme identifie les candidats qui correspondent parfaitement à vos critères techniques et humains.",
                icon: <Cpu className="h-6 w-6 text-white" />
              },
              {
                title: "Onboarding Culturel",
                desc: "Nos candidats sont préparés aux codes et à l'étiquette professionnelle japonaise pour une intégration fluide.",
                icon: <Briefcase className="h-6 w-6 text-white" />
              },
              {
                title: "Marque Employeur",
                desc: "Une page entreprise dédiée pour mettre en avant votre culture et attirer les meilleurs profils.",
                icon: <Users className="h-6 w-6 text-white" />
              }
            ].map((feature, i) => (
              <div key={i} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 h-14 w-14 rounded-2xl bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/20 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Website Interface Overview */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Aperçu de l'Interface du Site Web</h2>
            <p className="text-lg text-slate-600">
              Découvrez une plateforme moderne et rationalisée conçue pour l'efficacité.
            </p>
          </div>

          <div className="space-y-24">
            {[
              {
                img: uiHome,
                text: "Une interface d'accueil intuitive et centrée sur l'utilisateur.",
                title: "Page d'Accueil"
              },
              {
                img: uiOffers,
                text: "Un job board dynamique proposant des opportunités sélectionnées et une navigation fluide.",
                title: "Offres d'Emploi"
              },
              {
                img: uiDashboard,
                text: "Une interface backend complète pour une gestion transparente de l'acquisition de talents.",
                title: "Tableau de Bord Recruteur"
              },
              {
                img: uiCompanies,
                text: "Top Employeurs : Explorez les leaders de l'industrie qui recrutent actuellement.",
                title: "Annuaire des Entreprises"
              }
            ].map((item, i) => (
              <InterfaceItem key={i} item={item} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-16">Le Recrutement Étudiant en 4 Étapes Simples</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-8 left-0 w-full h-0.5 bg-slate-200 -z-10 transform translate-y-4"></div>

            {[
              { step: "01", title: "Publier", desc: "Créez votre offre de stage/emploi et construisez votre profil d'entreprise." },
              { step: "02", title: "Matcher", desc: "Recevez une liste instantanée de candidats motivés et matchés par IA." },
              { step: "03", title: "Recruter", desc: "Interrogez les meilleurs profils et faites votre sélection." },
              { step: "04", title: "Intégrer", desc: "Nous gérons la pré-formation culturelle et le suivi." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 relative">
                <div className="text-5xl font-bold text-slate-100 absolute top-4 right-4">{item.step}</div>
                <div className="h-10 w-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold mb-6 relative z-10">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Difference */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">Ça ne s'arrête pas au recrutement</h2>
            <p className="text-lg text-slate-600 mb-12 text-center">
              Un recrutement réussi repose sur une intégration réussie. Nos candidats ne sont pas "juste des étudiants" ; ce sont des professionnels préparés.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Pour le Candidat</h4>
                    <p className="text-slate-600 leading-relaxed">Formation complète sur l'étiquette des affaires japonaises (ex: hō-ren-sō), les attentes du lieu de travail et la culture. Comprend un suivi d'un mois après l'arrivée.</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Pour Votre Entreprise</h4>
                    <p className="text-slate-600 leading-relaxed">Vous recevez un membre d'équipe mieux préparé, plus autonome et rapidement opérationnel. Cela réduit considérablement la charge RH et augmente la rétention.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Story */}
      <section data-testid="success-story-section" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">Témoignage</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Franchir les Frontières vers le Japon</h2>
            <p className="text-lg text-slate-600">
              Découvrez comment un stagiaire ingénieur français a transformé un stage de 6 mois au Japon en une expérience qui a changé sa vie.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-72 lg:h-auto">
                  <img 
                    src={thomasPortrait} 
                    alt="Thomas, stagiaire ingénieur de Lyon" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent lg:bg-gradient-to-r"></div>
                  <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6">
                    <p className="text-white font-bold text-xl">Thomas</p>
                    <p className="text-white/80 text-sm">Stagiaire en ingénierie logicielle, originaire de Lyon</p>
                  </div>
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <blockquote className="text-lg text-slate-700 italic leading-relaxed mb-6 border-l-4 border-red-500 pl-4">
                    "Ce stage m'a appris l'importance du travail d'équipe et comment résoudre les problèmes de manière collaborative. Je suis vraiment content d'avoir choisi de venir au Japon."
                  </blockquote>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">Étudiant en école d'ingénieurs, Lyon, France</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">Stagiaire en développement iOS dans une startup japonaise</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">A choisi le Japon par curiosité pour sa culture</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 bg-slate-50 p-6 lg:p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={thomasExhibition} alt="Thomas lors d'une exposition" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Impact Concret</p>
                      <p className="text-slate-500 text-sm">A développé une app iOS et participé à des expositions</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={thomasFushimi} alt="Fushimi Inari Taisha" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Découverte Culturelle</p>
                      <p className="text-slate-500 text-sm">Exploration de Kyoto, Fushimi Inari, et plus</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-center justify-start md:justify-end">
                    <a 
                      href="https://medium.com/@CareCube_/crossing-borders-to-japan-thomass-journey-as-an-engineering-intern-d4e31a2848f3" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      data-testid="read-full-story-link"
                      className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors group"
                    >
                      Lire l'article complet
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-6xl mx-auto">
            {[
              {
                name: "Grégoire",
                role: "Étudiant en échange académique",
                school: "ICAM Strasbourg-Europe",
                location: "Shibaura Institute of Technology, Tokyo",
                quote: "Les universités japonaises offrent des opportunités pour explorer la science de pointe, la robotique et l'IA. Mon conseil : n'attendez pas, foncez !",
                img: gregoireJapan,
                link: "https://www.icam.fr/temoignage/gregoire-temoignage-dun-echange-academique-au-japon/"
              },
              {
                name: "Diego",
                role: "Stagiaire Analyste de Données",
                school: "UCLy / ESTRI Lyon",
                location: "MSC Japan, Tokyo",
                quote: "J'ai pu mettre en pratique les cours de langues et d'interculturalité dans un contexte concret. J'en ressors plus autonome et plus adaptable.",
                img: diegoTokyo,
                link: "https://www.ucly.fr/lience-lea-le-stage-de-diego-a-tokyo/"
              },
              {
                name: "Yoann",
                role: "Stagiaire Systèmes Embarqués & Réalisateur",
                school: "CESI Toulouse",
                location: "Startup IoT, Shibuya, Tokyo",
                quote: "Voyager seul à l'autre bout du monde est déstabilisant, mais ces expériences sont celles qui permettent de vraiment grandir.",
                img: yoannJapan,
                link: "https://www.cesi.fr/en/testimonials/yugen-international-internship-japan/"
              },
              {
                name: "Mirabelle",
                role: "Stagiaire Ingénieure Chimiste",
                school: "CPE Lyon",
                location: "Air Liquide Laboratories, Tsukuba",
                quote: "Cette expérience à l'étranger est le meilleur choix que je pouvais faire ! Partir loin permet de prendre un recul sur ce qu'on croyait universel.",
                img: mirabelleJapan,
                link: "https://www.cpe.fr/article/aa-international-partir-cgp-eti-stages-et-annee-de-cesure-temoignages-annees-de-cesure-mirabelle-denavit-japon/"
              }
            ].map((story, i) => (
              <a 
                key={i}
                href={story.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`story-card-${story.name.toLowerCase()}`}
                className="group bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden hover:shadow-xl hover:border-red-200 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={story.img} 
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                  <div className="absolute bottom-3 left-4">
                    <p className="text-white font-bold text-lg">{story.name}</p>
                    <p className="text-white/80 text-xs">{story.school}</p>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{story.role}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">{story.location}</p>
                  <blockquote className="text-sm text-slate-600 italic leading-relaxed border-l-2 border-red-300 pl-3">
                    "{story.quote}"
                  </blockquote>
                  <div className="mt-4 flex items-center gap-1 text-red-600 text-sm font-semibold group-hover:gap-2 transition-all">
                    Lire l'article <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Business Models */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Zéro Risque, Grande Flexibilité</h2>
            <p className="text-lg text-slate-600">Choisissez le plan qui correspond à la stratégie de votre entreprise. Pas de frais complexes, pas de coûts cachés.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card
              className="border-2 border-slate-200 hover:border-red-600 transition-colors cursor-pointer group"
              onClick={() => handleDemoClick("Demande d'abonnement annuel")}
            >
              <CardHeader>
                <CardTitle className="text-2xl group-hover:text-red-600 transition-colors">Abonnement Annuel</CardTitle>
                <CardDescription>Idéal pour des besoins de recrutement continus</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Plans Basic, Standard, Enterprise
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Publications d'offres mensuelles
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Gestionnaire de compte dédié
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card
              className="border-2 border-red-600 bg-white shadow-xl relative overflow-hidden cursor-pointer"
              onClick={() => handleDemoClick("Demande de frais au succès")}
            >
              <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">POPULAIRE</div>
              <CardHeader>
                <CardTitle className="text-2xl text-red-600">Frais au Succès</CardTitle>
                <CardDescription>Modèle de paiement par recrutement</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Pas de frais initiaux
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Payez uniquement en cas de succès
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Période d'essai sans risque
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">FAQ</h2>

          <Tabs defaultValue="Général" className="space-y-8">
            <div className="flex justify-center overflow-x-auto pb-4 scrollbar-hide">
              <TabsList className="bg-slate-100 p-1 rounded-full border border-slate-200 h-auto inline-flex">
                {[
                  "Général",
                  "Étudiants",
                  "Modèle Économique",
                  "Étapes Pratiques"
                ].map((cat) => (
                  <TabsTrigger
                    key={cat}
                    value={cat}
                    className="rounded-full px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-red-600 data-[state=active]:shadow-sm text-slate-600 transition-all"
                  >
                    {cat}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {[
              {
                category: "Général",
                items: [
                  {
                    q: "Internjin est-elle une agence de recrutement ?",
                    a: "Non. Internjin est une plateforme de matching dédiée exclusivement aux stages, avec une composante d'accompagnement pédagogique. L'objectif est la formation et l'échange interculturel, pas le placement permanent."
                  },
                  {
                    q: "Depuis quand Internjin existe-t-elle ?",
                    a: "Internjin est une startup en phase de lancement, fondée par deux étudiants français sur la base de leurs propres expériences de stage au Japon."
                  },
                  {
                    q: "Êtes-vous soutenus par des institutions françaises ?",
                    a: "Internjin travaille en lien avec des établissements d'enseignement supérieur français et recherche activement des partenariats institutionnels. Le modèle est entièrement aligné sur les exigences légales françaises en matière de stages."
                  }
                ]
              },
              {
                category: "Étudiants",
                items: [
                  {
                    q: "Quel est le niveau des étudiants que vous proposez ?",
                    a: "Les étudiants proviennent d'écoles de commerce, d'écoles d'ingénieurs, d'universités et d'institutions spécialisées françaises, du niveau Bachelor au niveau Master, dans la tech, le design, le marketing, la finance, les RH, et plus encore."
                  },
                  {
                    q: "Parlent-ils japonais ?",
                    a: "Certains oui, mais ce n'est pas obligatoire. Les étudiants capables de travailler en anglais sont également sélectionnés. Les candidatures peuvent être filtrées par critères linguistiques, et tous les étudiants reçoivent une préparation culturelle sur le Japon avant leur départ."
                  },
                  {
                    q: "L'étudiant viendra-t-il seul au Japon ?",
                    a: "Oui. L'étudiant gère son propre voyage et son logement, avec l'aide d'Internjin en amont. Internjin peut également mettre en relation les entreprises avec des ressources locales pour faciliter l'accueil et l'intégration de l'étudiant."
                  },
                  {
                    q: "Que se passe-t-il si le stage ne se passe pas bien ?",
                    a: "Internjin agit en tant que médiateur et intervient rapidement pour trouver une solution satisfaisante pour les deux parties. Dans les cas extrêmes, une fin anticipée du stage peut être organisée selon les termes de la convention."
                  },
                  {
                    q: "Comment les étudiants français gèrent-ils la culture de travail japonaise ?",
                    a: "Les étudiants français ont tendance à être directs et proactifs, ce qui peut différer des normes japonaises. Tous les étudiants reçoivent une préparation interculturelle couvrant les styles de communication, la hiérarchie, la ponctualité et l'étiquette professionnelle. Internjin reste disponible tout au long du stage pour soutenir les deux parties."
                  },
                  {
                    q: "Et si l'étudiant a un accident ou tombe malade au Japon ?",
                    a: "L'étudiant doit avoir une assurance santé et voyage personnelle organisée avant le départ. L'entreprise n'est pas responsable de la couverture santé personnelle. Les accidents du travail suivent les responsabilités décrites dans la convention de stage, et Internjin peut conseiller les deux parties."
                  },
                  {
                    q: "Peut-on recruter l'étudiant définitivement après le stage ?",
                    a: "Absolument, et cela arrive. Si les deux parties sont intéressées, l'entreprise peut offrir un poste à temps plein à la fin du stage. Internjin soutient pleinement ce résultat."
                  }
                ]
              },
              {
                category: "Business Model",
                items: [
                  {
                    q: "Combien cela coûte-t-il pour l'entreprise ?",
                    a: "Il n'y a aucune obligation légale de payer l'étudiant en vertu de la loi japonaise, mais offrir une gratification est fortement recommandé pour attirer les meilleurs candidats. Des frais de service sont également payés à Internjin pour le matching et le suivi ; une grille tarifaire détaillée est fournie lors de la réunion d'introduction."
                  },
                  {
                    q: "L'étudiant est-il un employé de l'entreprise ?",
                    a: "Non. L'étudiant est régi par une convention de stage, aucun contrat de travail n'est requis. La convention de stage est le seul document juridique nécessaire."
                  },
                  {
                    q: "Quelle est la durée minimale et maximale d'un stage ?",
                    a: "Les stages varient de 2 mois à 1 an, selon la disponibilité de l'étudiant, les besoins de l'entreprise et la portée du projet."
                  }
                ]
              },
              {
                category: "Étapes Pratiques",
                items: [
                  {
                    q: "Quelles démarches l'entreprise doit-elle entreprendre ?",
                    a: "Définissez vos besoins et les profils souhaités, signez la convention de stage, désignez un mentor interne et accueillez l'étudiant. Internjin gère la sélection des candidats, la gestion documentaire et le soutien continu."
                  },
                  {
                    q: "Combien de temps prend le processus de matching ?",
                    a: "La sélection et le matching prennent généralement 2 à 4 semaines à partir du moment où l'offre est publiée. Internjin recommande de planifier au moins 6 à 8 semaines avant la date de début souhaitée pour tenir compte des délais de visa et des calendriers académiques."
                  },
                  {
                    q: "Peut-on prolonger ou renouveler le stage si cela se passe bien ?",
                    a: "Oui, tant que cela reste dans le cadre académique de l'étudiant et que les deux parties sont d'accord. Toute prolongation doit être formalisée par un avenant à la convention de stage originale, qu'Internjin gérera."
                  },
                  {
                    q: "L'entreprise doit-elle parler français ?",
                    a: "Non. Internjin fournit une médiation bilingue, et tous les documents peuvent être fournis en anglais ou en japonais."
                  },
                  {
                    q: "Comment démarrer un partenariat avec Internjin ?",
                    a: "Contactez-nous simplement pour planifier un appel de découverte sans engagement. L'équipe élaborera une proposition adaptée aux besoins de l'entreprise."
                  }
                ]
              }
            ].map((category) => (
              <TabsContent key={category.category} value={category.category} className="animate-in fade-in-50 slide-in-from-bottom-2 duration-300">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  {category.items.map((item, j) => (
                    <AccordionItem key={j} value={`item-${j}`} className="border rounded-xl px-6 bg-slate-50 hover:bg-slate-100 transition-colors border-slate-100 shadow-sm data-[state=open]:bg-white data-[state=open]:shadow-md data-[state=open]:border-red-100">
                      <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-red-600 hover:no-underline py-4 text-lg">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 leading-relaxed pb-6 text-base">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Founders */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Rencontrez les Fondateurs</h2>
            <p className="text-lg text-slate-600">
              Créé par d'anciens stagiaires au Japon qui comprennent le parcours.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* P. Theotime */}
            <Card className="border-none shadow-lg bg-slate-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-24 w-24 bg-slate-200 rounded-full flex items-center justify-center mb-6 text-2xl font-bold text-slate-600 border-4 border-white shadow-sm">
                  PT
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">P. Theotime</h3>
                <p className="text-red-600 font-medium mb-6">Co-Fondateur</p>

                <div className="space-y-4 text-slate-600 w-full">
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <GraduationCap className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">Master en Robotique</p>
                      <p className="text-sm text-slate-500">CPE Lyon, France</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <Briefcase className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">Stage de 6 mois</p>
                      <p className="text-sm text-slate-500">BMW Japon (Tokyo)</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* D. Chahan */}
            <Card className="border-none shadow-lg bg-slate-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-24 w-24 bg-slate-200 rounded-full flex items-center justify-center mb-6 text-2xl font-bold text-slate-600 border-4 border-white shadow-sm">
                  DC
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">D. Chahan</h3>
                <p className="text-red-600 font-medium mb-6">Co-Fondateur</p>

                <div className="space-y-4 text-slate-600 w-full">
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <GraduationCap className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">Master en Robotique</p>
                      <p className="text-sm text-slate-500">CPE Lyon, France</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <Briefcase className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">Stage de 6 mois</p>
                      <p className="text-sm text-slate-500">Sound & Vision Technologies (Osaka)</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-500 text-red-400">Feuille de Route</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Étendre le Réseau à Toute l'Europe</h2>
            <p className="text-xl text-slate-300 font-light">
              Notre mission ne s'arrête pas à la France. Nous construisons le pont ultime entre les entreprises japonaises et les universités les plus prestigieuses d'Europe.
            </p>
            <div className="relative w-full max-w-5xl mx-auto mt-16 aspect-[16/9] rounded-xl overflow-hidden bg-slate-50 border border-slate-200 shadow-xl group">
              <img
                src={mapWorld}
                alt="Carte du Monde"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* East Asia Expansion */}
            <div className="mt-24 text-center">
              <Badge variant="outline" className="mb-4 border-blue-500 text-blue-400">Phase 2</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Expansion en Asie de l'Est</h2>
              <p className="text-xl text-slate-300 font-light max-w-3xl mx-auto mb-12">
                Notre vision inclut de relier l'Europe aux économies dynamiques tels que la Corée du Sud, Malaisie, Singapour et Chine.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { name: "Corée du Sud", img: flagKorea },
                  { name: "Malaisie", img: flagMalaysia },
                  { name: "Singapour", img: flagSingapore },
                  { name: "Chine", img: flagChina }
                ].map((country, i) => (
                  <div key={i} className="group relative rounded-xl overflow-hidden aspect-square border border-slate-700 bg-slate-800 shadow-lg hover:shadow-blue-500/20 hover:border-blue-500/50 transition-all duration-300">
                    <img src={country.img} alt={country.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent p-4">
                      <p className="font-bold text-white text-lg">{country.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomeFR;
